function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { usdK } from "../views/sales/exec-kit";
import { lapseLabel } from "../views/sales/sales-api";

/*
 * The four sales maps on one component (SALES_TERRITORY_MAPS.md §6):
 *   M1 dealer pins    circle markers sized by revenue, coloured by lapse band
 *   M2 store pins     markers coloured by type (store, outlet), pace ring when targets exist
 *   M3 rep coverage   hex polygons coloured by the owning rep; uncovered hatched, overlaps outlined
 *   M4 market heat    ZIP3 circles: fill = demand index, ring = booked index
 *
 * Leaflet 1.9 over a bundled US state outline (no tile vendor, no API key), loaded
 * lazily from the CDN on first mount so the bundle carries no map library and a
 * tenant without ZIP data never pays for it. Points arrive as plain
 * [{lat,lng,...}] (sales-api geoPoints), hexes as [{ring:[[lat,lng]...], reps, ...}].
 * No basemap key is needed; the tenant's Google Maps key is a later upgrade.
 *
 * props: coverage · dealers · stores · heat (null = layer absent) · defaultLayers
 *        onDealer(point) · onStore(point) · onRep(repCode) · onHex(hex) · onHeat(zip3)
 *        height (px) · fit (bool, default true) · compact (hides the layer control)
 */

const LEAFLET_VER = "1.9.4";
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.js`;
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VER}/dist/leaflet.css`;
// No third-party basemap (Carto tiles need an API key since 2025): the base is a
// bundled US state outline (Census cartographic boundaries, public domain) served
// from /data/us-states.json, styled from the theme so it reads in light and dark.
const STATES_URL = "/data/us-states.json";
let statesPromise = null;
const loadStates = () => statesPromise || (statesPromise = fetch(STATES_URL).then(r => r.ok ? r.json() : null).catch(() => null));
const BAND_COLORS = {
  healthy: "#14b8a6",
  watch: "#f0b051",
  at_risk: "#ff5a58",
  lapsing: "#b3383f",
  "": "#90a0b7"
};
const REP_PALETTE = ["#2f80ed", "#14b8a6", "#f0b051", "#9b59b6", "#e67e22", "#1abc9c", "#e84393", "#3d8b2f", "#c0392b", "#2c3e50", "#16a085", "#8e44ad"];
const UNCOVERED = "#ff5a58";
let _leaflet = null;
export function loadLeaflet() {
  if (_leaflet) return _leaflet;
  _leaflet = new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    if (window.L && window.L.map) {
      resolve(window.L);
      return;
    }
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const s = document.createElement("script");
    s.src = LEAFLET_JS;
    s.async = true;
    s.onload = () => window.L && window.L.map ? resolve(window.L) : reject(new Error("leaflet missing"));
    s.onerror = () => reject(new Error("leaflet failed to load"));
    document.head.appendChild(s);
  }).catch(e => {
    _leaflet = null;
    throw e;
  });
  return _leaflet;
}
const isDark = () => {
  try {
    const t = document.documentElement.getAttribute("data-theme");
    if (t === "dark") return true;
    if (t === "light") return false;
    return !!(window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches);
  } catch (e) {
    return false;
  }
};
const esc = s => String(s == null ? "" : s).replace(/[&<>"]/g, c => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  "\"": "&quot;"
})[c]);
export default class GeoMap extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "toggle", k => this.setState(s => ({
      layers: {
        ...s.layers,
        [k]: !s.layers[k]
      }
    })));
    const d = props.defaultLayers || {};
    let legendOpen = true;
    try {
      legendOpen = localStorage.getItem("plunk-map-legend") !== "closed";
    } catch (e) {/* private mode */}
    this.state = {
      legendOpen,
      ready: false,
      failed: false,
      layers: {
        coverage: d.coverage !== false,
        dealers: d.dealers !== false,
        stores: d.stores !== false,
        heat: !!d.heat
      }
    };
    this._el = /*#__PURE__*/React.createRef();
    this._groups = {};
  }
  componentDidMount() {
    this._alive = true;
    loadLeaflet().then(L => {
      if (!this._alive) return;
      this.L = L;
      this.init();
    }).catch(() => this._alive && this.setState({
      failed: true
    }));
  }
  componentWillUnmount() {
    this._alive = false;
    if (this.map) {
      try {
        this.map.remove();
      } catch (e) {/* already gone */}
      this.map = null;
    }
  }
  componentDidUpdate(prev, prevState) {
    if (!this.map) return;
    if (prev.coverage !== this.props.coverage || prev.dealers !== this.props.dealers || prev.stores !== this.props.stores || prev.heat !== this.props.heat || prevState.layers !== this.state.layers) {
      this.draw(prev.coverage !== this.props.coverage || prev.dealers !== this.props.dealers || prev.stores !== this.props.stores || prev.heat !== this.props.heat);
    }
  }
  init() {
    const L = this.L;
    if (!this._el.current) return;
    this.map = L.map(this._el.current, {
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: false,
      preferCanvas: true,
      minZoom: 3
    });
    const dark = isDark();
    this._el.current.style.background = dark ? "#1b1d22" : "#f6f5f2";
    loadStates().then(geo => {
      if (!geo || !this.map) return;
      this.states = L.geoJSON(geo, {
        interactive: false,
        style: () => ({
          color: dark ? "#4a4f58" : "#c9c6bf",
          weight: 1,
          fillColor: dark ? "#23262c" : "#ffffff",
          fillOpacity: 1
        })
      }).addTo(this.map);
      try {
        this.states.bringToBack();
      } catch (e) {/* no-op */}
    });
    this.map.setView([38.5, -96], 4);
    this.setState({
      ready: true
    }, () => this.draw(true));
  }
  repColors() {
    const cov = this.props.coverage;
    const colors = {};
    if (!cov) return colors;
    (cov.legend || []).forEach((l, i) => {
      colors[l.repCode] = l.color || REP_PALETTE[i % REP_PALETTE.length];
    });
    let i = Object.keys(colors).length;
    (cov.hexes || []).forEach(h => (h.reps || []).forEach(r => {
      if (!colors[r.repCode]) {
        colors[r.repCode] = REP_PALETTE[i % REP_PALETTE.length];
        i++;
      }
    }));
    return colors;
  }
  draw(fit) {
    const L = this.L;
    const map = this.map;
    if (!L || !map) return;
    Object.values(this._groups).forEach(g => {
      try {
        map.removeLayer(g);
      } catch (e) {/* none */}
    });
    this._groups = {};
    const {
      coverage,
      dealers,
      stores,
      heat,
      onDealer,
      onStore,
      onHex,
      onHeat
    } = this.props;
    const {
      layers
    } = this.state;
    const bounds = [];
    const colors = this.repColors();

    // M3 coverage hexes
    if (coverage && layers.coverage) {
      const g = L.layerGroup();
      (coverage.hexes || []).forEach(h => {
        const owner = (h.reps || [])[0];
        const uncovered = !owner || h.uncoveredZips > 0 && !(h.reps || []).length;
        const color = uncovered ? UNCOVERED : colors[owner.repCode] || REP_PALETTE[0];
        const style = {
          color: h.overlap ? "#232020" : color,
          weight: h.overlap ? 1.6 : 0.6,
          fillColor: color,
          fillOpacity: uncovered ? 0.22 : 0.42,
          dashArray: uncovered ? "3 3" : null
        };
        let shape = null;
        if (h.ring && h.ring.length >= 3) shape = L.polygon(h.ring, style);else if (h.lat != null && h.lng != null) shape = L.circle([h.lat, h.lng], {
          ...style,
          radius: 9000
        });
        if (!shape) return;
        const title = uncovered ? `Uncovered · ${h.uncoveredZips || h.zips || 0} ZIPs with dealers and no rep` : `${esc(owner.name || owner.repCode)} · ${h.zips || owner.zips || 0} ZIPs${h.overlap ? " · overlap" : ""}`;
        shape.bindTooltip(title, {
          sticky: true
        });
        if (onHex) shape.on("click", () => onHex(h));
        shape.addTo(g);
        if (h.ring) h.ring.forEach(pt => bounds.push(pt));else bounds.push([h.lat, h.lng]);
      });
      g.addTo(map);
      this._groups.coverage = g;
    }
    // M4 heat
    if (heat && layers.heat) {
      const g = L.layerGroup();
      heat.forEach(z => {
        const r = 8000 + (z.demandIndex || 0) * 600;
        const fill = L.circle([z.lat, z.lng], {
          radius: r,
          color: "transparent",
          fillColor: UNCOVERED,
          fillOpacity: 0.16 + (z.demandIndex || 0) / 250
        });
        // The orders ring only where something was booked: a zero-order area has fill only.
        const hasOrders = (z.booked || 0) > 0 || (z.bookedIndex || 0) > 0;
        const ring = hasOrders ? L.circle([z.lat, z.lng], {
          radius: 8000 + (z.bookedIndex || 0) * 600,
          color: "#2f80ed",
          weight: 2,
          fill: false,
          opacity: 0.9
        }) : null;
        const tip = `${esc(z.label)} · demand ${Math.round(z.demandIndex)} · ${hasOrders ? "booked " + Math.round(z.bookedIndex) : "no orders"} · gap ${Math.round(z.gap)}${z.uncovered ? " · uncovered" : ""}`;
        fill.bindTooltip(tip, {
          sticky: true
        });
        if (ring) ring.bindTooltip(tip, {
          sticky: true
        });
        if (onHeat) {
          fill.on("click", () => onHeat(z));
          if (ring) ring.on("click", () => onHeat(z));
        }
        fill.addTo(g);
        if (ring) ring.addTo(g);
        bounds.push([z.lat, z.lng]);
      });
      g.addTo(map);
      this._groups.heat = g;
    }
    // M1 dealer pins
    if (dealers && layers.dealers) {
      const g = L.layerGroup();
      const max = Math.max(1, ...dealers.map(p => p.revenue || 0));
      dealers.forEach(p => {
        const r = 3 + Math.sqrt((p.revenue || 0) / max) * 9;
        const m = L.circleMarker([p.lat, p.lng], {
          radius: r,
          color: "#fff",
          weight: 1,
          fillColor: BAND_COLORS[p.band || ""] || BAND_COLORS[""],
          fillOpacity: 0.92
        });
        m.bindTooltip(`<b>${esc(p.name || p.id)}</b><br>${usdK(p.revenue)}${p.orders ? ` · ${p.orders} orders` : ""}${p.band ? ` · ${esc(lapseLabel(p.band))}` : ""}${p.assignedRep ? `<br>${esc(typeof p.assignedRep === "object" ? p.assignedRep.name || p.assignedRep.repCode : p.assignedRep)}` : ""}${p.geoSource && p.geoSource !== "address" ? "<br><i>pin at ZIP centroid, approximate</i>" : ""}`, {
          sticky: true
        });
        if (onDealer) m.on("click", () => onDealer(p));
        m.addTo(g);
        bounds.push([p.lat, p.lng]);
      });
      g.addTo(map);
      this._groups.dealers = g;
    }
    // M2 store pins
    if (stores && layers.stores) {
      const g = L.layerGroup();
      const max = Math.max(1, ...stores.map(p => p.revenue || 0));
      stores.forEach(p => {
        const r = 6 + Math.sqrt((p.revenue || 0) / max) * 10;
        const pace = p.pace != null ? Number(p.pace) > 5 ? Number(p.pace) / 100 : Number(p.pace) : null;
        if (pace != null) {
          L.circleMarker([p.lat, p.lng], {
            radius: r + 5,
            color: pace >= 1 ? "#14b8a6" : "#ff5a58",
            weight: 3,
            fill: false,
            opacity: 0.9,
            dashArray: `${Math.round(Math.min(pace, 1.3) * 2 * Math.PI * (r + 5))} 999`
          }).addTo(g);
        }
        const m = L.circleMarker([p.lat, p.lng], {
          radius: r,
          color: "#fff",
          weight: 1.5,
          fillColor: String(p.type || "").toLowerCase() === "outlet" ? "#f0b051" : "#2f80ed",
          fillOpacity: 0.95
        });
        m.bindTooltip(`<b>${esc(p.name || p.id)}</b>${p.type ? ` · ${esc(p.type)}` : ""}<br>${usdK(p.revenue)}${pace != null ? ` · pace ${Math.round(pace * 100)}%` : ""}${p.geoSource && p.geoSource !== "address" && p.geoSource !== "feed" ? "<br><i>pin at ZIP centroid, approximate</i>" : ""}`, {
          sticky: true,
          permanent: false
        });
        if (onStore) m.on("click", () => onStore(p));
        m.addTo(g);
        bounds.push([p.lat, p.lng]);
      });
      g.addTo(map);
      this._groups.stores = g;
    }
    if (fit && this.props.fit !== false && bounds.length) {
      // Fit to the lower 48 when anything is there: one Alaska or Hawaii visitor would otherwise shrink the
      // mainland to a corner. Those points still draw; the view only frames the continental ones.
      const lower48 = bounds.filter(([lat, lng]) => lat >= 24 && lat <= 50 && lng >= -125 && lng <= -66);
      const fit = lower48.length ? lower48 : bounds;
      try {
        map.fitBounds(fit, {
          padding: [24, 24],
          maxZoom: fit.length === 1 ? 9 : 11
        });
      } catch (e) {/* degenerate bounds */}
    }
    if (fit) setTimeout(() => {
      try {
        map.invalidateSize();
      } catch (e) {/* unmounted */}
    }, 50);
  }
  render() {
    const {
      coverage,
      dealers,
      stores,
      heat,
      height = 420,
      compact,
      onRep
    } = this.props;
    const {
      failed,
      ready,
      layers
    } = this.state;
    const colors = this.repColors();
    const legend = coverage ? coverage.legend && coverage.legend.length ? coverage.legend : Object.keys(colors).map(k => ({
      repCode: k,
      name: k
    })) : [];
    const centroidOnly = [...(dealers || []), ...(stores || [])].length > 0 && ![...(dealers || []), ...(stores || [])].some(p => p.geoSource === "address");
    if (failed) return /*#__PURE__*/React.createElement("div", {
      className: "wa-empty"
    }, "The map could not load (the map library is fetched from the CDN on first use). Check the network and retry.");
    return /*#__PURE__*/React.createElement("div", {
      className: "sx-mapbox",
      style: {
        height
      }
    }, /*#__PURE__*/React.createElement("div", {
      ref: this._el,
      className: "sx-map",
      style: {
        height: "100%"
      }
    }), !ready ? /*#__PURE__*/React.createElement("div", {
      className: "sx-map-loading"
    }, "Loading map") : null, !compact ? /*#__PURE__*/React.createElement("div", {
      className: "sx-layers"
    }, coverage ? /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: layers.coverage,
      onChange: () => this.toggle("coverage")
    }), " Rep coverage (ZIP)") : null, dealers ? /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: layers.dealers,
      onChange: () => this.toggle("dealers")
    }), " Dealer pins") : null, heat ? /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: layers.heat,
      onChange: () => this.toggle("heat")
    }), " Market heat") : null, stores ? /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: layers.stores,
      onChange: () => this.toggle("stores")
    }), " Store pins") : null) : null, /*#__PURE__*/React.createElement("div", {
      className: `sx-legend${this.state.legendOpen ? "" : " closed"}`
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-legend-toggle",
      title: this.state.legendOpen ? "Hide the legend" : "Show the legend",
      "aria-expanded": this.state.legendOpen,
      onClick: () => {
        const open = !this.state.legendOpen;
        this.setState({
          legendOpen: open
        });
        try {
          localStorage.setItem("plunk-map-legend", open ? "open" : "closed");
        } catch (e) {/* private mode */}
      }
    }, this.state.legendOpen ? "Legend ▾" : "Legend ▸"), this.state.legendOpen && coverage && layers.coverage ? legend.slice(0, 10).map(l => /*#__PURE__*/React.createElement("div", {
      key: l.repCode,
      className: onRep ? "clickable" : "",
      title: `ZIP areas assigned to ${l.name || l.repCode}${l.zips ? ` (${l.zips} ZIPs)` : ""}; click to open the rep`,
      onClick: () => onRep && onRep(l.repCode)
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        background: colors[l.repCode] || REP_PALETTE[0]
      }
    }), " ", l.name || l.repCode, " ", l.zips ? /*#__PURE__*/React.createElement("span", {
      className: "hint"
    }, l.zips, " ZIPs") : null)) : null, this.state.legendOpen && coverage && layers.coverage ? /*#__PURE__*/React.createElement("div", {
      title: "ZIP areas with dealers or website demand and no rep assigned"
    }, /*#__PURE__*/React.createElement("i", {
      className: "hatch"
    }), " uncovered") : null, this.state.legendOpen && coverage && layers.coverage && (coverage.hexes || []).some(h => h.overlap) ? /*#__PURE__*/React.createElement("div", {
      title: "ZIP areas assigned to more than one rep on the same channel with no priority set"
    }, /*#__PURE__*/React.createElement("i", {
      className: "outline"
    }), " overlap") : null, this.state.legendOpen && dealers && layers.dealers ? [["healthy", "healthy", "Dealer ordering on its usual cadence"], ["watch", "watch", "Dealer a little past its usual gap between orders"], ["at_risk", "at risk", "Dealer well past its usual gap: a call this week"], ["lapsing", "lapsing", "Dealer far past its usual gap: likely lost without a visit"]].map(([k, l, t]) => /*#__PURE__*/React.createElement("div", {
      key: k,
      title: t + " · pin size is revenue in the window"
    }, /*#__PURE__*/React.createElement("i", {
      className: "dot",
      style: {
        background: BAND_COLORS[k]
      }
    }), " ", l)) : null, this.state.legendOpen && stores && layers.stores ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      title: "A store from the store feed; ring thickness is pace against target"
    }, /*#__PURE__*/React.createElement("i", {
      className: "dot",
      style: {
        background: "#2f80ed"
      }
    }), " store"), /*#__PURE__*/React.createElement("div", {
      title: "An outlet location from the store feed"
    }, /*#__PURE__*/React.createElement("i", {
      className: "dot",
      style: {
        background: "#f0b051"
      }
    }), " outlet")) : null, this.state.legendOpen && heat && layers.heat ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      title: "Website sessions from this area in the window, ranked 0 to 100 against every other area; stronger fill = more browsing"
    }, /*#__PURE__*/React.createElement("i", {
      className: "dot",
      style: {
        background: UNCOVERED,
        opacity: 0.6
      }
    }), " web interest (fill)"), /*#__PURE__*/React.createElement("div", {
      title: "Revenue booked at dealers in this area in the window, ranked the same way; thicker ring = more orders. Strong fill with a thin ring is browsing without orders"
    }, /*#__PURE__*/React.createElement("i", {
      className: "ring"
    }), " orders booked (ring)")) : null), centroidOnly ? /*#__PURE__*/React.createElement("div", {
      className: "sx-attr"
    }, "Pins are ZIP centroids, approximate \xB7 Census ZCTA 2020 \xB7 GeoNames (CC BY)") : null);
  }
}